import React from 'react';
import clsx from 'clsx';
import type { ModuleDefinition } from './moduleRegistry';
import type { AuditedStatementHeader, AuditedStatementSet, AuditedStatementSlice, ReportData } from '../data/types';
import { ScreenSection } from '../primitives/ScreenPrimitives';
import {
    CASH_FLOW_INDIRECT_TITLE,
    CASH_FLOW_INDIRECT_DISCLOSURE,
    CASH_FLOW_INDIRECT_LAYOUT,
    CASH_FLOW_LAYOUT,
    keepComputablePeriods,
    type AuditedRowSpec,
} from '../../Dashboard/DashboardModules/AuditedFinancialsVisualisation/auditedStatementLayout';
import { appendUnmatchedRows } from '../../Dashboard/DashboardModules/Financials/components/ProvisionalFinanicals/provisionalStatementLayout';
import {
    AUDITED_MODULE_TITLE,
    AUDITED_VERSIONS,
    CLASSIFICATION_COLUMN_KEY,
    OUTSIDE_DISPLAYED_RANGE_LABEL,
    PERIOD_STATUS_NOT_AVAILABLE,
    PROVISIONAL_SECTION_TITLE,
    REPORT_ANCHOR_ATTR,
    reportAnchorId,
    STATEMENT_VERSION_SEPARATOR,
    VERSION_BAND_SUFFIX,
    YEARS_NOT_SHOWN_LABEL,
    subOptionOn,
    REPORT_MODULE_ID,
} from '../constants';
import { renderableStatements } from '../data/normalizersExt';
import styles from './AuditedStatements.module.scss';

const YEAR_HEADER_PATTERN = /^\d{4}$|^FY/i;

const BS_SECTIONS: Array<{ name: string; match: RegExp }> = [
    { name: 'Equity & Liabilities', match: /equity|liabilit|borrowing|payab|provis|reserv|share/i },
    { name: 'Assets', match: /asset|receiv|invent|cash|invest|intang|tangib|advance|deposit/i },
];

const isBalanceSheet = (name: string) => /balance\s*sheet/i.test(name);
const isPnL = (name: string) => /p\s*&\s*l|profit|income/i.test(name);
const isRatios = (name: string) => /ratio/i.test(name);

const groupBalanceSheet = (rows: AuditedStatementSlice['rows']) => {
    const groups: Record<string, typeof rows> = {};
    rows.forEach((r) => {
        const bucket = BS_SECTIONS.find((s) => s.match.test((r.label ?? '') + ' ' + r.classificationName))?.name ?? 'Other';
        (groups[bucket] ??= []).push(r);
    });
    return groups;
};

const formatCell = (v?: string) => (v == null || v === '' ? '—' : v);

const yearColumns = (slice: AuditedStatementSlice) =>
    keepComputablePeriods({ headers: slice.headers })
        .headers.filter((h) => YEAR_HEADER_PATTERN.test(h.key) || h.key !== CLASSIFICATION_COLUMN_KEY)
        .slice(-6);

// Move aggregated ("Total …") rows to the bottom of each group so the running
// figures read first and the summary lands last — easier to scan. Stable sort:
// non-aggregated keep their relative order, aggregated rows are appended.
const totalsLast = <T extends { isAggregated?: boolean }>(rows: T[]): T[] =>
    [...rows].sort((a, b) => Number(!!a.isAggregated) - Number(!!b.isAggregated));

// ──────────────────────────── Screen ────────────────────────────

const StatementBandRow: React.FC<{ label: string; span: number }> = ({ label, span }) => (
    <tr>
        <td colSpan={span} className={styles.BandCell}>
            {label}
        </td>
    </tr>
);

const StatementDataRow: React.FC<{
    label: string;
    aggregated?: boolean;
    indented?: boolean;
    values?: Record<string, string>;
    cols: AuditedStatementHeader[];
}> = ({ label, aggregated, indented, values, cols }) => (
    <tr className={clsx({ [styles.RowAggregated]: aggregated })}>
        <td className={clsx(styles.Td, styles.TdLabel, { [styles.TdLabelIndented]: indented, [styles.TdLabelAggregated]: aggregated })}>
            {label}
        </td>
        {cols.map((h) => (
            <td key={h.key} className={styles.Td}>
                {formatCell(values?.[h.key])}
            </td>
        ))}
    </tr>
);

// Indirect is checked first: its title also matches /cash/i.
const cashFlowLayoutFor = (slice: AuditedStatementSlice, provisional: boolean): AuditedRowSpec[] | null => {
    const base = slice.name === CASH_FLOW_INDIRECT_TITLE ? CASH_FLOW_INDIRECT_LAYOUT : /cash/i.test(slice.name) ? CASH_FLOW_LAYOUT : null;
    if (!base) return null;
    return provisional ? appendUnmatchedRows(base, { rows: slice.rows }) : base;
};

export const LayoutRows: React.FC<{ slice: AuditedStatementSlice; layout: AuditedRowSpec[]; cols: AuditedStatementHeader[] }> = ({
    slice,
    layout,
    cols,
}) => {
    const bySrc = new Map(slice.rows.map((r) => [r.classificationName, r]));
    return (
        <>
            {layout
                .filter((row) => !(row.hideWhenAbsent && row.src && !bySrc.has(row.src)))
                .map((row, i) =>
                    row.type === 'group' || row.type === 'sub' ? (
                        <StatementBandRow key={i} label={row.label} span={cols.length + 1} />
                    ) : (
                        <StatementDataRow
                            key={i}
                            label={row.label}
                            aggregated={row.type === 'subtotal'}
                            values={row.src ? bySrc.get(row.src)?.values : undefined}
                            cols={cols}
                        />
                    ),
                )}
        </>
    );
};

const ClassifiedRows: React.FC<{ slice: AuditedStatementSlice; cols: AuditedStatementHeader[] }> = ({ slice, cols }) => {
    const useGroups = isBalanceSheet(slice.name);
    const rawGroups = useGroups ? groupBalanceSheet(slice.rows) : { [slice.name]: slice.rows };
    const groups = Object.fromEntries(Object.entries(rawGroups).map(([k, v]) => [k, totalsLast(v)]));
    return (
        <>
            {Object.entries(groups).map(([groupName, rows]) => (
                <React.Fragment key={groupName}>
                    {useGroups ? <StatementBandRow label={groupName} span={cols.length + 1} /> : null}
                    {rows.map((r, i) => (
                        <StatementDataRow
                            key={i}
                            label={r.classificationName}
                            aggregated={r.isAggregated}
                            indented={!!r.subLabel}
                            values={r.values}
                            cols={cols}
                        />
                    ))}
                </React.Fragment>
            ))}
        </>
    );
};

const ScreenStatementTable: React.FC<{ slice: AuditedStatementSlice; provisional: boolean }> = ({ slice, provisional }) => {
    const cols = yearColumns(slice);
    const layout = cashFlowLayoutFor(slice, provisional);
    const isRatioTable = isRatios(slice.name);
    return (
        <table className={styles.Table}>
            <thead>
                <tr>
                    <th className={clsx(styles.Th, styles.ThLabel)}>{isRatioTable ? 'Ratio' : 'Classification'}</th>
                    {cols.map((h) => (
                        <th key={h.key} className={styles.Th}>
                            {h.name}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {layout ? <LayoutRows slice={slice} layout={layout} cols={cols} /> : <ClassifiedRows slice={slice} cols={cols} />}
            </tbody>
        </table>
    );
};

const omittedYearNote = (h: AuditedStatementHeader): string =>
    `${h.name} — ${h.statusReason || (h.periodStatus === PERIOD_STATUS_NOT_AVAILABLE ? h.periodStatus : OUTSIDE_DISPLAYED_RANGE_LABEL)}`;

const StatementBlock: React.FC<{ slice: AuditedStatementSlice; isFirst: boolean; provisional: boolean; versionLabel?: string }> = ({
    slice,
    isFirst,
    provisional,
    versionLabel,
}) => {
    const isCashFlowIndirect = slice.name === CASH_FLOW_INDIRECT_TITLE;
    const shownKeys = isCashFlowIndirect ? new Set(yearColumns(slice).map((h) => h.key)) : null;
    const omittedHeaders = shownKeys ? slice.headers.filter((h) => h.key !== CLASSIFICATION_COLUMN_KEY && !shownKeys.has(h.key)) : [];
    return (
        <div className={isFirst ? styles.StatementBlockFirst : styles.StatementBlock}>
            <div className={styles.StatementTitle}>
                {versionLabel ? `${slice.name}${STATEMENT_VERSION_SEPARATOR}${versionLabel}` : slice.name}
            </div>
            {isCashFlowIndirect && <div className={styles.Disclosure}>{CASH_FLOW_INDIRECT_DISCLOSURE}</div>}
            <ScreenStatementTable slice={slice} provisional={provisional} />
            {omittedHeaders.length > 0 && (
                <div className={styles.YearsNotShown}>
                    <div>
                        {YEARS_NOT_SHOWN_LABEL}: {omittedHeaders.map(omittedYearNote).join('; ')}
                    </div>
                </div>
            )}
        </div>
    );
};

const printedVersionSets = (
    audited: NonNullable<ReportData['auditedStatements']>,
    options?: Record<string, boolean>,
): AuditedStatementSet[] =>
    AUDITED_VERSIONS.filter((v) => audited[v.hasKey] && subOptionOn(options, v.id)).map((v) => ({
        id: v.id,
        label: v.label,
        statements: renderableStatements(audited[v.statementsKey] ?? []),
    }));

const hasUnlabelledStatements = (props?: ReportData['auditedStatements']) => renderableStatements(props?.unlabelled ?? []).length > 0;

const ScreenAuditedStatements: React.FC<{ data: ReportData; index: string; options?: Record<string, boolean> }> = ({
    data,
    index,
    options,
}) => {
    const audited = data.auditedStatements;
    const versionSets = audited ? printedVersionSets(audited, options) : [];
    const hasAuditedVersions = !!audited && AUDITED_VERSIONS.some((v) => audited[v.hasKey]);
    const isProvisional = !hasAuditedVersions && !hasUnlabelledStatements(audited);
    const sets: AuditedStatementSet[] = isProvisional
        ? [{ statements: renderableStatements(data.provisionalStatements?.unlabelled ?? []) }]
        : hasAuditedVersions
        ? versionSets
        : [{ statements: renderableStatements(audited?.unlabelled ?? []) }];
    const printedCount = sets.reduce((n, set) => n + set.statements.length, 0);
    if (printedCount === 0) return null;

    const versionSuffix = isProvisional ? 'Provisional (management-supplied)' : sets.flatMap((set) => set.label ?? []).join(' & ');
    const countLabel = `${printedCount} statement${printedCount > 1 ? 's' : ''}`;
    const subtitle = versionSuffix ? `${countLabel} · ${versionSuffix}` : countLabel;
    return (
        <ScreenSection
            kicker="Section · Statements"
            title={isProvisional ? PROVISIONAL_SECTION_TITLE : AUDITED_MODULE_TITLE}
            subtitle={subtitle}
            index={index}
        >
            <div className={styles.AmountsNote}>All amounts in ₹</div>
            {/* All slices rendered one after another with a sub-heading each — no
                tab switching. The previous tabbed view only mounted the active
                slice, so the server PDF (which captures this DOM) silently lost
                every other statement. */}
            {sets.map((set, setIndex) => (
                <React.Fragment key={set.label ?? 'all'}>
                    {set.label ? (
                        <div
                            className={clsx(styles.VersionBand, { [styles.VersionBandNewPage]: setIndex > 0 })}
                            {...{ [REPORT_ANCHOR_ATTR]: reportAnchorId(REPORT_MODULE_ID.auditedStatements, set.id ?? '') }}
                        >
                            {`${set.label} ${VERSION_BAND_SUFFIX}`}
                        </div>
                    ) : null}
                    {set.statements.map((slice, i) => (
                        <StatementBlock
                            key={slice.name}
                            slice={slice}
                            isFirst={i === 0}
                            provisional={isProvisional}
                            versionLabel={set.label}
                        />
                    ))}
                </React.Fragment>
            ))}
        </ScreenSection>
    );
};

export const auditedStatementsModule: ModuleDefinition = {
    meta: {
        id: 'auditedStatements' as any,
        title: AUDITED_MODULE_TITLE,
        description: 'Balance Sheet, P&L, Cashflow & Financial Ratios from audited filings.',
        dataSource: 'polus/insights/financialsAuditedDataRange',
    },
    // Module renders if EITHER audited or provisional statements are present —
    // the body falls through to provisional when audited is empty.
    hasData: (d) =>
        AUDITED_VERSIONS.some((v) => !!d.auditedStatements?.[v.hasKey]) ||
        hasUnlabelledStatements(d.auditedStatements) ||
        hasUnlabelledStatements(d.provisionalStatements),
    Screen: ScreenAuditedStatements,
};
